import { Sale } from '@utilities/types';
import React, { useState } from 'react';
import { RotateCcw, ChevronDown, ChevronUp, Printer, Download, CreditCard } from 'lucide-react';
import { PaymentMethodeEnum } from '@utilities/enums';

export interface SaleTableProps {
  paginatedSales: Sale[];
  onReturnClick?: (sale: Sale) => void;
  onPrintClick?: (sale: Sale) => void;
  onDownloadInvoiceClick?: (sale: Sale) => void;
}

export const SaleTable: React.FC<SaleTableProps> = ({ paginatedSales, onReturnClick, onPrintClick, onDownloadInvoiceClick }) => {
  const tableHeaderRow = [
    "Date",
    "N°. commande",
    "Total à payer",
    "Total Payé",
    "Payé par",
    "Détails",
    "Facture",
    "Actions"
  ];

  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});

  const toggleRowDetails = (saleId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [saleId]: !prev[saleId]
    }));
  };

  const getPaymentMethodBadgeStyle = (methode?: string) => {
    switch (methode) {
      case PaymentMethodeEnum.esp:
        return 'bg-green-100 text-green-800';
      case PaymentMethodeEnum.mobilMoney:
        return 'bg-orange-100 text-orange-800';
      case PaymentMethodeEnum.cb:
        return 'bg-blue-100 text-blue-800';
      case PaymentMethodeEnum.cheque:
        return 'bg-purple-100 text-purple-800';
      case PaymentMethodeEnum.credit:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour obtenir les méthodes de paiement d'une vente (gère les deux systèmes)
  const getPaymentMethods = (sale: Sale) => {
    // Nouveau système avec paiements multiples
    if (sale.paymentMethods && sale.paymentMethods.length > 0) {
      return sale.paymentMethods;
    }
    // Ancien système avec un seul paiement
    if (sale.paymentMethode) {
      return [{
        methode: sale.paymentMethode.methode,
        amount: sale.totalPay,
        mobilePayment: sale.paymentMethode.mobilePayment,
        _creditID: sale.paymentMethode._creditID
      }];
    }
    return [];
  };

  // Fonction pour afficher les badges de paiement
  const renderPaymentBadges = (sale: Sale) => {
    const methods = getPaymentMethods(sale);

    if (methods.length === 0) {
      return (
        <span className="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
          N/A
        </span>
      );
    }

    if (methods.length === 1) {
      return (
        <span className={`px-2 py-1 rounded-full text-sm ${getPaymentMethodBadgeStyle(methods[0].methode)}`}>
          {methods[0].methode}
        </span>
      );
    }

    // Paiements multiples
    return (
      <div className="flex flex-wrap gap-1">
        <span className="px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800 font-medium">
          {methods.length} modes
        </span>
      </div>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="table w-full">
        <thead className="bg-gray-100">
          <tr>
            {tableHeaderRow.map((header, index) => (
              <th
                key={index}
                className={`py-3 px-4 ${index >= 2 && index <= 3 ? 'text-right' : 'text-left'}`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedSales.length === 0 ? (
            <tr>
              <td colSpan={8} className="py-8 text-center text-gray-500">
                Aucune vente trouvée
              </td>
            </tr>
          ) : (
            paginatedSales.map((sale) => (
              <React.Fragment key={sale._id}>
                <tr className="hover:bg-gray-50 border-b">
                  {/* Date */}
                  <td className="py-3 px-4">
                    {new Date(sale.createAt).toLocaleDateString('fr-FR')}
                  </td>

                  {/* N°. commande */}
                  <td className="py-3 px-4 font-medium">
                    {sale.orderNumber}
                  </td>

                  {/* Total à payer */}
                  <td className="py-3 px-4 text-right">
                    {sale.totalToPay.toLocaleString('fr-FR')} Fcfa
                  </td>

                  {/* Total Payé */}
                  <td className="py-3 px-4 text-right">
                    {sale.totalPay.toLocaleString('fr-FR')} Fcfa
                  </td>

                  {/* Payé par */}
                  <td className="py-3 px-4">
                    {renderPaymentBadges(sale)}
                  </td>

                  {/* Détails */}
                  <td className="py-3 px-4">
                    <button
                      onClick={() => sale._id && toggleRowDetails(sale._id)}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      {sale._id && expandedRows[sale._id] ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Masquer
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Voir
                        </>
                      )}
                    </button>
                  </td>

                  {/* Facture */}
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onPrintClick?.(sale)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Imprimer"
                      >
                        <Printer className="w-4 h-4" />
                        <span className="text-xs">Imprimer</span>
                      </button>
                      <button
                        onClick={() => onDownloadInvoiceClick?.(sale)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                        title="Télécharger la facture"
                      >
                        <Download className="w-4 h-4" />
                        <span className="text-xs">Télécharger</span>
                      </button>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="py-3 px-4">
                    <button
                      onClick={() => onReturnClick?.(sale)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                      title="Gérer les retours"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span className="text-xs">Retour</span>
                    </button>
                  </td>
                </tr>

                {/* Expanded Details Row */}
                {sale._id && expandedRows[sale._id] && (
                  <tr className="bg-gray-50">
                    <td colSpan={8} className="py-4 px-6">
                      <div className="space-y-4">
                        {/* Section Header */}
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                            <span className="w-1 h-5 bg-primary rounded-full"></span>
                            Produits dans le panier ({sale.basket.length})
                          </h4>
                          <span className="text-sm text-gray-500">
                            Commande du {new Date(sale.createAt).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>

                        {/* Products Grid */}
                        <div className="grid gap-3">
                          {sale.basket.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                            >
                              {/* Product Image Placeholder */}
                              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                {item?.product?.images?.[0]?.fileDir ? (
                                  <img
                                    src={item.product.images[0].fileDir}
                                    alt={item.product.name}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                ) : (
                                  <span className="text-2xl text-gray-400">📦</span>
                                )}
                              </div>

                              {/* Product Info */}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">
                                  {item?.product?.name || 'Produit inconnu'}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 mt-1.5">
                                  {/* Size Badge */}
                                  {item?.selectedSize && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-md">
                                      <span className="text-blue-400">Taille:</span> {item.selectedSize}
                                    </span>
                                  )}
                                  {/* Color with Dot */}
                                  {item?.selectedColor && (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-50 text-gray-700 text-xs font-medium rounded-md">
                                      <span
                                        className="w-3.5 h-3.5 rounded-full border border-gray-300 shadow-sm flex-shrink-0"
                                        style={{ backgroundColor: item.selectedColor }}
                                        title={item.selectedColor}
                                      />
                                      <span className="text-gray-500">Couleur</span>
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Quantity & Price */}
                              <div className="text-right flex-shrink-0">
                                <div className="flex items-center gap-2 justify-end">
                                  <span className="text-xs text-gray-500">Qté:</span>
                                  <span className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 text-primary font-bold rounded-lg">
                                    {item?.quantity || 1}
                                  </span>
                                </div>
                                {item?.price && (
                                  <p className="text-sm font-medium text-gray-700 mt-1">
                                    {(item.price * (item.quantity || 1)).toLocaleString('fr-FR')} Fcfa
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Footer Info */}
                        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-200">
                          {sale.promotion && sale.promotion.value > 0 && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                              <span className="text-green-500">🏷️</span>
                              <span className="text-green-700 text-sm font-medium">
                                Promotion: -{sale.promotion.value}{sale.promotion.type === 'percentage' ? '%' : ' Fcfa'}
                              </span>
                            </div>
                          )}
                          {(sale.totalToPay - sale.totalPay) > 0 && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                              <span className="text-red-500">⚠️</span>
                              <span className="text-red-700 text-sm font-medium">
                                Reste à payer: {(sale.totalToPay - sale.totalPay).toLocaleString('fr-FR')} Fcfa
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Payment Methods Details */}
                        {getPaymentMethods(sale).length > 0 && (
                          <div className="pt-3 border-t border-gray-200">
                            <h5 className="font-semibold text-gray-700 flex items-center gap-2 mb-3">
                              <CreditCard className="w-4 h-4 text-gray-500" />
                              Détail des paiements
                            </h5>
                            <div className="grid gap-2">
                              {getPaymentMethods(sale).map((payment, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100"
                                >
                                  <div className="flex items-center gap-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadgeStyle(payment.methode)}`}>
                                      {payment.methode}
                                    </span>
                                    {payment.mobilePayment && (
                                      <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <span>📱</span> {payment.mobilePayment}
                                      </span>
                                    )}
                                    {payment.transactionRef && (
                                      <span className="text-xs text-gray-400">
                                        Réf: {payment.transactionRef}
                                      </span>
                                    )}
                                  </div>
                                  <span className="font-semibold text-gray-800">
                                    {payment.amount.toLocaleString('fr-FR')} Fcfa
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
